import { useState } from 'react';
import { STATUSES } from '../plan/arc';
import type { Status } from '../plan/arc';
import type { NewArc } from './newArc';
import { BackGlyph, CloseGlyph } from './parts';
import { useLoadingShown } from '../shell/Loading';

export interface RecentRecording {
  path: string;
  date: string | null;
  face: string;
}

/**
 * Making an arc, in the arcs panel's slot. Step one asks what it is about, or
 * what it comes out of; step two asks its name, with what was typed first
 * moved in as its description. Nothing is drafted for you: no suggested name,
 * no generated body.
 */
export function NewArcFlow({
  recordings,
  taken,
  onClose,
  onCreate,
}: {
  /** The most recent recordings, newest first; null while they are read. */
  recordings: RecentRecording[] | null;
  /** True for a name that is already an arc's. */
  taken: (name: string) => boolean;
  onClose: () => void;
  onCreate: (arc: NewArc) => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [about, setAbout] = useState('');
  const [picks, setPicks] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** What was last moved into the description, so going back and on again
   *  moves the new text without overwriting what was written over it. */
  const [moved, setMoved] = useState('');
  const [folded, setFolded] = useState(true);
  const [status, setStatus] = useState<Status>('active');
  const [startWith, setStartWith] = useState('');
  const [doneWhen, setDoneWhen] = useState('');

  const canGoOn = about.trim() !== '' || picks.length > 0;
  const nameIsTaken = taken(name);
  const canCreate = name.trim() !== '' && !nameIsTaken;
  const byPath = new Map((recordings ?? []).map((r) => [r.path, r]));

  const toggle = (path: string) =>
    setPicks((p) => (p.includes(path) ? p.filter((x) => x !== path) : [...p, path]));

  const next = () => {
    if (!canGoOn) return;
    if (description === moved) setDescription(about);
    setMoved(about);
    setStep(2);
  };

  const create = () => {
    if (!canCreate) return;
    onCreate({ name, description, sources: picks, status, startWith, doneWhen });
  };

  return (
    <div className="nook-newarc">
      <div className="nook-panel-head">
        {step === 2 && (
          <button type="button" className="nook-btn nook-icon" aria-label="Back" onClick={() => setStep(1)}>
            <BackGlyph />
          </button>
        )}
        <h2 className="nook-panel-title">New arc</h2>
        <button type="button" className="nook-btn nook-icon nook-newarc-close" aria-label="Close" onClick={onClose}>
          <CloseGlyph />
        </button>
      </div>

      {step === 1 ? (
        <div className="nook-newarc-body">
          <textarea
            className="nook-newarc-about"
            aria-label="What is it about?"
            placeholder="What is it about?"
            rows={3}
            autoFocus
            value={about}
            onChange={(e) => setAbout(e.target.value)}
          />
          <div className="nook-newarc-listhead">
            <span className="nook-newarc-or">Or pick what it comes out of</span>
            <span className="nook-newarc-label">Recent</span>
          </div>
          {recordings === null ? (
            <ReadingRecent />
          ) : (
            <div className="nook-newarc-recordings">
              {recordings.map((r) => {
                const picked = picks.includes(r.path);
                return (
                  <div key={r.path} className={`nook-newarc-rec${picked ? ' picked' : ''}`}>
                    <span className="nook-newarc-rec-text">
                      {r.date && <span className="nook-date">{r.date}</span>}
                      <span className="nook-newarc-face">{r.face}</span>
                    </span>
                    <button
                      type="button"
                      className="nook-newarc-add"
                      aria-pressed={picked}
                      onClick={() => toggle(r.path)}
                    >
                      {picked ? '✓ Added' : '＋ Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="nook-newarc-body">
          <textarea
            className="nook-newarc-name"
            aria-label="Name"
            placeholder="Name it"
            rows={2}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value.replace(/\n/g, ' '))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                create();
              }
            }}
          />
          {nameIsTaken && <p className="nook-newarc-taken">An arc already has this name.</p>}

          <span className="nook-newarc-label">Description</span>
          <textarea
            className="nook-newarc-description"
            aria-label="Description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {picks.length > 0 && (
            <>
              <span className="nook-newarc-label">Comes out of</span>
              <div className="nook-newarc-chips">
                {picks.map((path) => (
                  <span key={path} className="nook-newarc-chip">
                    <span className="nook-newarc-dot" aria-hidden="true" />
                    <span className="nook-date">{byPath.get(path)?.date ?? path.replace(/^.*\//, '')}</span>
                    <button type="button" aria-label="Take it out" onClick={() => toggle(path)}>
                      <CloseGlyph />
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}

          <button
            type="button"
            className="nook-newarc-fold"
            aria-expanded={!folded}
            onClick={() => setFolded((f) => !f)}
          >
            Starts {status} · first step · ends when {folded ? '⌄' : '⌃'}
          </button>
          {!folded && (
            <div className="nook-newarc-more">
              <label className="nook-newarc-field">
                <span className="nook-newarc-label">Starts</span>
                <select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="nook-newarc-field">
                <span className="nook-newarc-label">First step</span>
                <input value={startWith} onChange={(e) => setStartWith(e.target.value)} />
              </label>
              <label className="nook-newarc-field">
                <span className="nook-newarc-label">Ends when</span>
                <input value={doneWhen} onChange={(e) => setDoneWhen(e.target.value)} />
              </label>
            </div>
          )}
        </div>
      )}

      <div className="nook-newarc-foot">
        {step === 1 ? (
          <button type="button" className="nook-newarc-go" disabled={!canGoOn} onClick={next}>
            Next
          </button>
        ) : (
          <>
            <span className="nook-newarc-lands">Lands at the end of this nook's plan</span>
            <button type="button" className="nook-newarc-go" disabled={!canCreate} onClick={create}>
              Create arc
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ReadingRecent() {
  return useLoadingShown() ? <p className="nook-panel-empty">Reading the recent recordings…</p> : null;
}
